import { Box, Divider, Typography } from '@mui/material'
import { StarRateRounded } from '@mui/icons-material'

const JobFitCard = ({ data }) => {
  const getJobFitScore = (jobFit) => {
    const ratings = {
      5: { stars: 5, text: 'Strongly fit for this job' },
      4: { stars: 4, text: 'Well fit for this job' },
      3: { stars: 3, text: 'Averagely fit for this job' },
      2: { stars: 2, text: 'Partially fit for this job' },
      1: { stars: 1, text: 'Minimum fit for this job' },
    }

    const { stars, text } = ratings[jobFit] || ratings['1']

    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box
          display="flex"
          flexDirection="row"
          alignItems={'center'}
          justifyContent={'center'}
          marginBottom={0.5}
          {...(stars === 1 && {
            px: 2,
            py: 1,
            mb: 2,
          })}
        >
          {[...Array(5)].map((_, index) => (
            <StarRateRounded
              key={index}
              color={index < stars ? 'yellow-01' : 'gray-line'}
              sx={{
                fontSize:
                  index === 2
                    ? 40
                    : index === 1 || index === 3
                    ? 35
                    : index === 0 || index === 4
                    ? 30
                    : 30,
              }}
            />
          ))}
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 'bold',
            color: 'white.main',
          }}
        >
          {text}
        </Typography>
      </Box>
    )
  }

  const message = getJobFitScore(String(data?.overall_match_score))

  // --------------------- Get match score ------------------------------ //
  const getMatchScore = (matchScore) => {
    const ratings = {
      5: { stars: 5, text: 'Strongly fit' },
      4: { stars: 4, text: 'Well fit' },
      3: { stars: 3, text: 'Averagely fit' },
      2: { stars: 2, text: 'Partially fit' },
      1: { stars: 1, text: 'Minimum fit' },
    }

    const { stars, text } = ratings[matchScore] || ratings['1']

    return (
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'medium',
              color: 'light-gray-text.main',
              backgroundColor: 'gray-background.main',
              padding: 1,
            }}
          >
            {text}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="row" marginBottom={0.5}>
          {[...Array(5)].map((_, index) => (
            <StarRateRounded
              key={index}
              color={index < stars ? 'yellow-01' : 'gray-line'}
              sx={{
                fontSize: 30,
              }}
            />
          ))}
        </Box>
      </Box>
    )
  }
  // -------------------------------------------------------------------------------- //

  const messageJobsRequirement = getMatchScore(String(data?.specific_requirements_score))

  const messageSkillsMatch = getMatchScore(String(data?.skills_match_score))

  const messageExperienceRelevance = getMatchScore(String(data?.experience_relevance_score))

  const messageEducationalBackground = getMatchScore(String(data?.educational_background_score))

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          padding: '0px 30px 30px 30px',
          display: 'flex',
          flexDirection: 'column',
          gap: '42px',
          backgroundImage: 'white.main',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            width: '100%',
            height: '350px',
            backgroundImage:
              'linear-gradient(161.69deg, #EF6617 31.18%, #F3BF3A 88.34%, #F27C19 146.6%)',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            top: '48px',
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {message}
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            sx={{ color: 'text.white', marginTop: '16px' }}
          >
            {data?.summary}
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            borderRadius: '4.5px',
            border: '1px solid #E5E5E5',
            backgroundColor: 'white.main',
            top: '32px',
          }}
        >
          <Box style={{ width: '100%' }} sx={{ width: 'full' }}>
            <Box
              sx={{
                paddingTop: '20px',
                paddingBottom: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              {/* -------------------------- Jobs requirement -------------------------- */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  marginLeft: '20px',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Jobs requirement match
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'light-gray-text.main', fontWeight: 'regular' }}
                >
                  {data?.specific_requirements_description}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '24px',
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'light-gray-text.main', fontWeight: 'regular', width: '100%' }}
                >
                  {messageJobsRequirement}
                </Typography>
              </Box>
              <Divider
                color="light-gray-text.main"
                sx={{ marginLeft: '20px', marginRight: '20px', opacity: 0.1 }}
              />
              {/* --------------------------------------------------------------------- */}

              {/* --------------------------- Skills match ---------------------------- */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  marginLeft: '20px',
                  marginTop: '24px',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Skills match
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'light-gray-text.main', fontWeight: 'regular' }}
                >
                  {data?.skills_match_description}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '24px',
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'light-gray-text.main', fontWeight: 'regular', width: '100%' }}
                >
                  {messageSkillsMatch}
                </Typography>
              </Box>
              <Divider
                color="light-gray-text.main"
                sx={{ marginLeft: '20px', marginRight: '20px', opacity: 0.1 }}
              />
              {/* --------------------------------------------------------------------- */}

              {/* ----------------------- Experience relevance ------------------------ */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  marginLeft: '20px',
                  marginTop: '24px',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Experience relevance
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'light-gray-text.main', fontWeight: 'regular' }}
                >
                  {data?.experience_relevance_description}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '24px',
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'light-gray-text.main', fontWeight: 'regular', width: '100%' }}
                >
                  {messageExperienceRelevance}
                </Typography>
              </Box>
              <Divider
                color="light-gray-text.main"
                sx={{ marginLeft: '40px', marginRight: '40px', opacity: 0.1 }}
              />
              {/* ----------------------------------------------------------------------- */}

              {/* ----------------------- Educational background ------------------------ */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  marginLeft: '20px',
                  marginTop: '24px',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Educational background
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'light-gray-text.main', fontWeight: 'regular' }}
                >
                  {data?.educational_background_description}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  // marginBottom: '24px',
                  marginLeft: '20px',
                  marginRight: '20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'light-gray-text.main', fontWeight: 'regular', width: '100%' }}
                >
                  {messageEducationalBackground}
                </Typography>
              </Box>
              {/* --------------------------------------------------------------------- */}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
export default JobFitCard
